<script setup>

import {ref, useAttrs} from "vue";
import Navigation from "@/Pages/Homepage/cards/Navigation.vue";
import Footer from "@/Pages/Homepage/cards/Footer.vue";

const attrs = useAttrs();

const emailRevealed = ref(false);
const emailMasked = ref(null);
function revealEmail() {
	emailRevealed.value = true;
	emailMasked.value = 'support@' + window.location.hostname
}

</script>

<template>
	<div class="min-h-screen relative">

		<!-- Navigation -->
		<Navigation :auth="attrs.auth"/>

		<!-- Hero Section -->
		<main class="max-w-4xl mx-auto px-6 pt-20 pb-16 grid gap-4 font-dmsans">
			<h1 class="text-4xl font-funnel">Privacy Policy</h1>
			<p>Effective as of 1/1/2024</p>

			<div class="p-6 bg-gray-900 text-white rounded-lg gap-6 grid">
				<h3 class="text-2xl font-funnel">
					Before we get into the details...
				</h3>
				<p>SlideCub understands presentations can be sensitive, and you shouldn't share anything that you aren't comfortable with, such as presentations with highly sensitive information.</p>
				<p>When you do share presentations with us (for the purposes of an AI slide review), we authenticate with Google on your behalf to secure view your presentation. Your slides are then processed through Google's Gemini large-language model, with feedback added directly into your presentation via Google provided endpoints. SlideCub and Google are the only two entities that are used to process your request.</p>
				<p><span class="font-black">Your presentations are <span class="underline">not</span> used to train AI models</span>, but human reviewers may review them to assess the effectiveness of our service. We take steps to protect your privacy as a part of this process, including disassociating information of you or your company from your presentation.</p>
				<p>We don't publish or share your presentation, but we may use it to improve our services. We may store slide thumbnails, metadata, and other usage statistics to improve your experience. If you contact us, such as by email, we use the information you provide for our reply. If you buy from us, we also keep data for purposes like bookkeeping and invoicing.</p>
				<p>If you want to learn more, you can find our privacy policy below.</p>

			</div>


			<p>
				This Privacy Policy describes how SlideCub ("we",“us” or "our") processes personal information that we collect through our digital or online properties or services that link to this Privacy Policy (including as applicable, our website, marketing activities (including in relation to business prospects), live events and other activities described in this Privacy Policy (collectively, the “Service”)). SlideCub may provide additional or supplemental privacy policies to individuals for specific products or services that we offer at the time we collect personal information.
			</p>
			<p>
				SlideCub provides a platform for extending and enabling functionality associated with digital presentations, including organization, optimization, design elements, and digital feedback. This Privacy Policy does not apply to information that we process on behalf of our business customers while providing the SlideCub platform to them. Our use of information that we process on behalf of our business customers may be governed by our agreements with such customers. If you have concerns regarding your personal information that we process on behalf of a business customer, please direct your concerns to that enterprise customer.
			</p>
			<h3 class="italic">
				<span class="font-black">NOTICE TO EUROPEAN USERS:</span> Please see the <a href="#eu-users-notice" class="text-blue-600 underline">Notice to European Users</a> section for additional information for individuals located in the European Economic Area or United Kingdom (which we refer to as “Europe”, and “European” should be understood accordingly) below.
			</h3>

			<nav class="mb-4">
				<h3 class="text-2xl font-funnel">Index</h3>
				<ul class="text-blue-600 underline list-disc pl-12 mt-4">
					<li><a href="#personal-information-collect">Personal information we collect</a></li>
					<li><a href="#personal-information-use">How we use your personal information</a></li>
					<li><a href="#personal-information-share">How we share your personal information</a></li>
					<li><a href="#use-of-human-reviewers">Use of human reviewers</a></li>
					<li><a href="#other-sites-and-services">Other sites and services</a></li>
					<li><a href="#security">Security</a></li>
					<li><a href="#international-data-transfers">International data transfers</a></li>
					<li><a href="#children">Children</a></li>
					<li><a href="#changes">Changes to this Privacy Policy</a></li>
					<li><a href="#contact">How to contact us</a></li>
					<li><a href="#eu-users-notice">Notice to European Users</a></li>
				</ul>
			</nav>

			<section id="personal-information-collect" class="grid gap-6">
				<a href="#personal-information-collect">
					<h3 class="text-2xl font-funnel mt-8">
						Personal information we collect
					</h3>
				</a>
				<p>
					<span class="font-black">Information you provide to us.</span> Personal information you may provide to us through the Service or otherwise includes:
				</p>
				<ul class="list-disc pl-12">
					<li><span class="font-black">Contact data</span>, such as your first and last name, email address, billing and mailing addresses, professional title and company name, and phone number.</li>
					<li><span class="font-black">Demographic data</span>, such as your city, state, country of residence, postal code, and age.</li>
					<li><span class="font-black">Profile data</span>, such as your username and password.</li>
					<li><span class="font-black">Communications data</span> based on our exchanges with you, including when you contact us through the Service, social media, or otherwise.</li>
					<li><span class="font-black">Transactional data</span>, such as information relating to or needed to complete your orders on or through the Service, including payment details, order numbers and transaction history.</li>
					<li><span class="font-black">Marketing data</span>, such as your preferences for receiving our marketing communications and details about your engagement with them.</li>
					<li><span class="font-black">User-generated content</span>, such as photos, images, videos, presentations, and other content or information that you generate, transmit, or otherwise make available on the Service, as well as associated metadata.</li>
					<li><span class="font-black">Other data</span> not specifically listed here, which we will use as described in this Privacy Policy or as otherwise disclosed at the time of collection.</li>
				</ul>
				<p>
					<span class="font-black">Third-party sources.</span> We may combine personal information we receive from you with personal information we obtain from other sources, such as:
				</p>
				<ul class="list-disc pl-12">
					<li><span class="font-black">Public sources</span>, such as government agencies, public records, social media platforms, and other publicly available sources.</li>
					<li><span class="font-black">Private sources</span>, such as your employer, data providers, or associated corporate tools (e.g., Google Workspace, Microsoft 365, etc.)</li>
					<li><span class="font-black">Our affiliate partners</span>, such as our affiliate network provider and publishers, influencers, and promoters who participate in our paid affiliate programs.</li>
					<li><span class="font-black">Marketing partners</span>, such as joint marketing partners and event co-sponsors.</li>
					<li><span class="font-black">Third-party services</span>, such as social media services, that you use to log into, or otherwise link to, your Service account. This data may include your username, profile picture and other information associated with your account on that third-party service that is made available to us based on your account settings on that service.</li>
				</ul>
				<p>
					<span class="font-black">Automatic data collection.</span>  We, our service providers, and our business partners may automatically log information about you, your computer or mobile device, and your interaction over time with the Service, our communications and other online services, such as:
				</p>
				<ul class="list-disc pl-12">
					<li><span class="font-black">Log data</span>, including your internet protocol (IP) address, operating system, browser type, browser ID, the URL you entered and the referring page/campaign, date/time of your visit, the time you spent on our Service and any errors that may occur during your visit. </li>
					<li><span class="font-black">Analytics Data</span>, including the electronic path you take to our Service, through and when exiting our Service, as well as your usage and activity on our Service, such as the links, objects, products and benefits you view, click or otherwise interact with (also known as “Clickstream Data”). Our emails may also contain tracking pixels that identify if and when you have opened an email that we have sent you, how many times you have read it and whether you have clicked on any links in that email. This helps us measure the effectiveness of our marketing email campaigns, make the emails we send to you more relevant to your interests and to understand if you have opened and read any important administrative emails we might send you. </li>
					<li><span class="font-black">Location Data</span>, including your general geographic location (no more specific than city/ country) based on the Log Data we collect.</li>
				</ul>
			</section>

			<section id="personal-information-use" class="grid gap-6">
				<a href="#personal-information-use">
					<h3 class="text-2xl font-funnel mt-8">
						How we use your personal information
					</h3>
				</a>
				<p>We may use your personal information for the following purposes or as otherwise described at the time of collection:</p>

				<p><span class="font-black">Service delivery and operations</span>. We may use your personal information to:</p>
				<ul class="list-disc pl-12">
					<li>provide, operate and improve the Service and our business;</li>
					<li>personalize the service, including remembering the devices from which you have previously logged in and remembering your selections and preferences as you navigate the Service;</li>
					<li>communicate with you about the Service, including by sending Service-related announcements, updates, security alerts, and support and administrative messages;</li>
					<li>communicate with you about events in which you participate;</li>
					<li>understand your needs and interests, and personalize your experience with the Service and our communications; and</li>
					<li>provide support for the Service, and respond to your requests, questions and feedback.</li>
				</ul>

				<p><span class="font-black">Research and development.</span> We may use your personal information for research and development purposes, including to analyze and improve the Service and our business and to develop new products and services.. As part of these activities, we may create aggregated, de-identified and/or anonymized data from personal information we collect. We make personal information into de-identified or anonymized data by removing information that makes the data personally identifiable to you. We may use this aggregated, de-identified or otherwise anonymized data and share it with third parties for our lawful business purposes, including to analyze and improve the Service and promote our business.</p>

				<p><span class="font-black">Marketing</span>. We, our service providers and our third-party advertising partners may collect and use your personal information for the following marketing purposes:</p>
				<ul class="list-disc pl-12">
					<li>Direct marketing. We may send you direct marketing communications, including our newsletter where you have provided your consent. You may opt-out of our marketing communications as described in the Opt-out of marketing section below.</li>
					<li>Events. We may use your personal information to administer events that we organize or sponsor.</li>
					<li>Service improvement and analytics. We may use your personal information to analyze your usage of the Service, improve the Service, improve the rest of our business, help us understand user activity on the Service, including which pages are most and least visited and how visitors move around the Service, as well as user interactions with our emails, and to develop new products and services.</li>
				</ul>

				<p><span class="font-black">Compliance and protection</span>. We may use your personal information to:</p>
				<ul class="list-disc pl-12">
					<li>comply with applicable laws, lawful requests, and legal process, such as to respond to subpoenas, investigations or requests from government authorities, and to comply with our anti-money laundering, Know-Your-Customer, and/or similar obligations;</li>
					<li>protect our, your or others’ rights, privacy, safety or property (including by making and defending legal claims);</li>
					<li>audit our internal processes for compliance with legal and contractual requirements or our internal policies;</li>
					<li>enforce the terms and conditions that govern the Service; and</li>
					<li>prevent, identify, investigate and deter fraudulent, harmful, unauthorized, unethical or illegal activity, including cyberattacks and identity theft.</li>
				</ul>

				<p><span class="font-black">With your consent</span>. In some cases, we may specifically ask for your consent to collect, use or share your personal information, such as when required by law.</p>

				<h3 class="text-2xl font-funnel mt-8">
					Retention
				</h3>

				<p>We generally retain personal information to fulfill the purposes for which we collected it, including for the purposes of satisfying any legal, accounting, or reporting requirements, to establish or defend legal claims, or for fraud prevention purposes. To determine the appropriate retention period for personal information, we may consider factors such as the amount, nature, and sensitivity of the personal information, the potential risk of harm from unauthorized use or disclosure of your personal information, the purposes for which we process your personal information and whether we can achieve those purposes through other means, and the applicable legal requirements.</p>
				<p>When we no longer require the personal information we have collected about you, we may either delete it, anonymize it, or isolate it from further processing.</p>
			</section>

			<section id="personal-information-share" class="grid gap-6">
				<a href="#personal-information-share">
					<h3 class="text-2xl font-funnel mt-8">
						How we share your personal information
					</h3>
				</a>
				<p><span class="font-black">We may share your personal information with the following parties and as otherwise described in this Privacy Policy, in other applicable notices, or at the time of collection.</span></p>
<!--				<p><span class="font-black">Affiliates.</span> Our corporate parent, subsidiaries, and affiliates.</p>-->
				<p><span class="font-black">Service providers.</span> Third parties that provide services on our behalf or help us operate the Service or our business (such as hosting, information technology, customer support, email delivery, marketing, consumer research and website analytics).</p>
				<p><span class="font-black">Payment processors.</span> Any payment card information you use to make a purchase on the Service is collected and processed directly by our payment processors, such as Stripe. Stripe may use your payment data in accordance with its privacy policy, <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy.</a></p>
<!--				<p><span class="font-black">Third parties designated by you.</span> We may share your personal information with third parties where you have instructed us or provided your consent to do so. We will share personal information that is needed for these other companies to provide the services that you have requested.</p>-->
				<p><span class="font-black">Business partners.</span> For example, third parties with whom we co-sponsor events and those with whom we jointly offer services.</p>
				<p><span class="font-black">Professional advisors.</span> Professional advisors, such as lawyers, auditors, bankers and insurers, where necessary in the course of the professional services that they render to us.</p>
				<p><span class="font-black">Authorities and others.</span> Law enforcement, government authorities, and private parties, as we believe in good faith to be necessary or appropriate for the Compliance and protection purposes described above.</p>
				<p><span class="font-black">Business transferees.</span> We may disclose personal information in the context of actual or prospective business transactions (e.g., investments in SlideCub, financing of SlideCub, public stock offerings, or the sale, transfer or merger of all or part of our business, assets or shares), for example, we may need to share certain personal information with prospective counterparties and their advisers. We may also disclose your personal information to an acquirer, successor, or assignee of SlideCub as part of any merger, acquisition, sale of assets, or similar transaction, and/or in the event of an insolvency, bankruptcy, or receivership in which personal information is transferred to one or more third parties as one of our business assets.</p>

				<section id="use-of-human-reviewers" class="grid gap-6">
					<a href="#use-of-human-reviewers">
						<h3 class="text-2xl font-funnel mt-8">
						Use of human reviewers
						</h3>
					</a>
				<p>To help with quality and improve our products (such as the AI slider view which uses generative machine-learning models), human reviewers may read and process your shared presentations. We take steps to protect your privacy as part of this process. This includes disassociating you and your company, and other relevant identifying information, from your shared presentation before reviewers see or process them. Please don’t share presentations which contain confidential information or any data you wouldn’t want a reviewer to see.</p>

				<p><span class="font-black">Paid Services.</span> When you use Paid Services, including, for example, the paid quota of the AI Presentation Review, SlideCub doesn't use your presentation (including associated system instructions, cached content, and other metadata) to improve our products. For Paid Services, SlideCub logs usage for a limited period of time, solely for the purpose of detecting violations of the prohibited uses and any required legal or regulatory disclosures. This data may be stored transiently or cached in any country in which SlideCub utilizes for hosting.</p>
				</section>

				<h3 class="text-2xl font-funnel mt-8">
					Your choices
				</h3>
				<p><span class="font-black">Opt-out of communications.</span> You may opt-out of marketing-related emails by following the opt-out or unsubscribe instructions at the bottom of the email, or by contacting us. Please note that if you choose to opt-out of marketing-related emails, you may continue to receive service-related and other non-marketing emails.</p>
				<p><span class="font-black">Do Not Track.</span> Some Internet browsers may be configured to send “Do Not Track” signals to the online services that you visit. To find out more about “Do Not Track,” please visit <a target="_blank" rel="noopener noreferrer" href="https://www.allaboutdnt.com">https://www.allaboutdnt.com.</a></p>
				<p><span class="font-black">Declining to provide information.</span> We need to collect personal information to provide certain services. If you do not provide the information we identify as required or mandatory, we may not be able to provide those services.</p>
				<p><span class="font-black">Linked third-party platforms.</span> If you choose to connect to the Service through your social media account or other third-party platform, you may be able to use your settings in your account with that platform to limit the information we receive from it. If you revoke our ability to access information from a third-party platform, that choice will not apply to information that we have already received from that third party, but may limit your ability to interact or utilize SlideCub's products and services.</p>

			</section>

			<section id="other-sites-and-services" class="grid gap-6">
				<a href="#other-sites-and-services">
					<h3 class="text-2xl font-funnel mt-8">
						Other sites and services
					</h3>
				</a>
				<p>The Service may contain links to websites, mobile applications, and other online services operated by third parties. In addition, our content may be integrated into web pages or other online services that are not associated with us. These links and integrations are not an endorsement of, or representation that we are affiliated with, any third party. We do not control websites, mobile applications or online services operated by third parties, and we are not responsible for their actions. We encourage you to read the privacy policies of the other websites, mobile applications and online services you use.</p>
			</section>

			<section id="security" class="grid gap-6">
					<a href="#security">
						<h3 class="text-2xl font-funnel mt-8">
						Security
						</h3>
					</a>
					<p>
						We employ a number of technical, organizational and physical safeguards designed to protect the personal information we collect. However, security risk is inherent in all internet and information technologies and we cannot guarantee the security of your personal information.
					</p>
				</section>

			<section id="international-data-transfers" class="grid gap-6">
				<a href="#international-data-transfers">
					<h3 class="text-2xl font-funnel mt-8">
						International data transfers
					</h3>
				</a>
				<p>
					We may use service providers that operate in other countries. Your personal information may be transferred to the United States or other locations where privacy laws may not be as protective as those in your state, province, or country.
				</p>
				<p class="italic">
					Users in Europe should review the <a href="#eu-users-notice" class="text-blue-600 underline">Notice to European Users</a> section for information on international data transfers from Europe.
				</p>
			</section>

			<section id="children" class="grid gap-6">
				<a href="#children">
					<h3 class="text-2xl font-funnel mt-8">
						Children
					</h3>
				</a>
				<p>
					The Service is not intended for use by anyone under 16 years of age. If you are a parent or guardian of a child from whom you believe we have collected personal information in a manner prohibited by law, please contact us using the details in the ‘How to contact us’ section. If we learn that we have collected personal information through the Service from a child without the consent of the child’s parent or guardian as required by law, we will comply with applicable legal requirements to delete the information.
				</p>
			</section>

			<section id="changes" class="grid gap-6">
				<a href="#changes">
					<h3 class="text-2xl font-funnel mt-8">
						Changes to this Privacy Policy
					</h3>
				</a>
				<p>
					We reserve the right to modify this Privacy Policy at any time. If we make material changes to this Privacy Policy, we will notify you by updating the date of this Privacy Policy and posting it on the Service or other appropriate means. Any modifications to this Privacy Policy will be effective upon our posting the modified version (or as otherwise indicated at the time of posting). In all cases, your use of the Service after the effective date of any modified Privacy Policy indicates your acknowledging that the modified Privacy Policy applies to your interactions with the Service and our business.
				</p>
			</section>

			<section id="contact" class="grid gap-6">
				<a href="#contact">
					<h3 class="text-2xl font-funnel mt-8">
						How to contact us
					</h3>
				</a>
				<p>
					If you have questions or comments about this notice, you may email us at: <span @click="revealEmail" :class="[emailRevealed ? '' : 'italic text-blue-500 underline']">{{ emailRevealed ? emailMasked : 'click to reveal.' }}</span>
				</p>
			</section>

			<section id="eu-users-notice" class="grid gap-6">
				<a href="#eu-users-notice">
					<h3 class="text-2xl font-funnel mt-8">
						Notice to European Users
					</h3>
				</a>
				<p><span class="font-black">Where this Notice to European Users applies.</span> The information provided in this “Notice to European Users” section applies only to individuals in the United Kingdom and the European Economic Area (i.e., “Europe” as defined at the top of this Privacy Policy).</p>
				<p><span class="font-black">Personal information.</span> References to “personal information” in this Privacy Policy should be understood to include a reference to “personal data” as defined in the “GDPR” (i.e., the General Data Protection Regulation 2016/679 (“EU GDPR”) and the EU GDPR as it forms part of the laws of the United Kingdom (“UK GDPR”). In the GDPR, “personal data” means information about individuals from which they are either directly identified or can be identified.</p>
				<p><span class="font-black">Controller.</span> SlideCub is the controller in respect of the processing of your personal information covered by this Privacy Policy for purposes of the GDPR. See the ‘How to contact us’ section above for our contact details.</p>

				<h3 class="text-2xl font-funnel mt-8">
					Our legal bases for processing
				</h3>

				<p>In respect of each of the purposes for which we use your personal information, the GDPR requires us to ensure that we have a “legal basis” for that use. Our legal bases for processing your personal information described in this Privacy Policy are listed below.
				</p>

				<ul class="list-disc pl-12">
					<li>Where we need to perform a contract, we are about to enter into or have entered into with you (<strong>“Contractual Necessity”</strong>).</li>
					<li>Where it is necessary for our legitimate interests and your interests and fundamental rights do not override those interests (<strong>“Legitimate Interests”</strong>). More detail about the specific legitimate interests pursued in respect of each Purpose we use your personal information for is set out in the table below.</li>
					<li>Where we need to comply with a legal or regulatory obligation (<strong>“Compliance with Law”</strong>).</li>
					<li>Where we have your specific consent to carry out the processing for the Purpose in question (<strong>“Consent”</strong>).</li>
				</ul>

				<table class="table-auto border-collapse border border-slate-300">
					<thead class="css-7luvuw">
						<tr class="bg-gray-200">
							<td class="border border-slate-300 px-3 py-3.5 font-black ">Purpose</td>
							<td class="border border-slate-300 px-3 py-3.5 font-black w-1/3">Categories of personal information involved</td>
							<td class="border border-slate-300 px-3 py-3.5 font-black ">Legal basis</td>
						</tr>
					</thead>
					<tbody class="text-sm">
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Service delivery and operations</strong></td>
							<td class="p-2">
								<ul role="list" class="list-disc list-inside">
									<li>Contact data</li>
									<li>Demographic data</li>
									<li>Profile Data</li>
									<li>Communications data</li>
									<li>Transactional data</li>
									<li>User-generated content</li>
									<li>Data from Third Party Services</li>
									<li>Device data</li>
								</ul>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc list-inside">
									<li class="css-0">Contractual Necessity</li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Research and development </strong></td>
							<td class="p-2">
								<ul role="list" class="list-disc list-inside">
									<li>Contact data</li>
									<li>Demographic data</li>
									<li>Communications data</li>
									<li>User-generated content</li>
									<li>Device data</li>
									<li>Online activity data</li>
									<li>Communication interaction data</li>
								</ul>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4">
									<li>
										<p>
										<strong>Legitimate Interests</strong>
										</p>
										<p>We have legitimate interest in improving our business by understanding more about how end users make use of our Services and platform and training our artificial intelligence models to optimize our Services</p>
									</li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Direct marketing</strong></td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4">
									<li>Contact data</li>
									<li>Demographic data</li>
									<li>Communications data</li>
									<li>Marketing data</li>
								</ul>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4 space-y-2">
									<li>
										<p><strong>Legitimate Interests</strong></p>
										<p>We have a legitimate interest in promoting our operations and goals as an organisation and sending marketing communications for that purpose</p>
									</li>
									<li><strong>Consent</strong>, in circumstances or in jurisdictions where consent is required under applicable data protection laws to the sending of any given marketing communications
									</li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Events</strong></td>
							<td class="p-2">
								<ul role="list" class="list-disc list-inside">
									<li>Contact data</li>
									<li>Demographic data</li>
									<li>Communications data</li>
									<li>Marketing data</li>
								</ul>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4 space-y-2">
									<li><p>
										<strong>Contractual Necessity</strong> to administer the event (including communicating with you as and where necessary)
									</p></li>
									<li><p>
										<strong>Consent</strong>, in circumstances or in jurisdictions where consent is required under applicable data protection laws to the sending of any given marketing communications
									</p></li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Service improvement and analytics</strong></td>
							<td class="p-2">
								<ul role="list" class="list-disc list-inside">
									<li>Contact data</li>
									<li>Demographic data</li>
									<li>Communications data</li>
									<li>User-generated content</li>
									<li>Device data</li>
									<li>Online activity data</li>
									<li>Communication interaction data</li>
								</ul>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4 space-y-2">
									<li><p>
										<strong>Legitimate Interests</strong></p>
										<p>We have a legitimate interest in providing you with a good service, which is personalised to you and that remembers your selections and preferences</p>
									</li>
									<li>
										<strong>Consent</strong>, in respect of any optional cookies used for this purpose
									</li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Compliance and protection</strong></td>
							<td class="p-2">

									<p>Any and all data types relevant in the circumstances</p>

							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4 space-y-2">
									<li><p>
										<strong>Compliance with Law</strong></p></li>
									<li><p>
										<strong>Legitimate Interests</strong></p>
										<p>Where Compliance with Law is not applicable, we have a legitimate interest in participating in, supporting, and following legal process and requests, including through co-operation with authorities. We may also have a legitimate interest of ensuring the protection, maintenance, and enforcement of our rights, property, and/or safety</p>
									</li>
								</ul>
							</td>
						</tr>
						<tr class="border border-slate-300">
							<td class="p-2"><strong>Further uses</strong></td>
							<td class="p-2">
								<p>Any and all data types relevant in the circumstances</p>
							</td>
							<td class="p-2">
								<ul role="list" class="list-disc pl-4 space-y-2">
									<li><p>The original legal basis relied upon, if the relevant further use is compatible with the initial purpose for which the personal information was collected</p>
									</li>
									<li><p>
										<strong>Consent</strong>, if the relevant further use is not compatible with the initial purpose for which the personal information was collected
									</p></li>
								</ul>
							</td>
						</tr>
					</tbody>
				</table>

				<h3 class="text-2xl font-funnel mt-8">
					Other info
				</h3>
				<p>
					<strong>No sensitive personal information.</strong>We ask that you not provide us with any sensitive personal information (e.g., information related to racial or ethnic origin, political opinions, religion or other beliefs, health, biometrics or genetic characteristics, criminal background or trade union membership) on or through the services, or otherwise to us. If you provide us with any sensitive personal information when you use the services, you must consent to our processing and use of such sensitive personal information in accordance with this Privacy Policy. If you do not consent to our processing and use of such sensitive personal information, you must not submit such sensitive personal information through our services.
				</p>
				<p><strong>No Automated Decision-Making and Profiling</strong>. As part of the Service, we do not engage in automated decision-making and/or profiling, which produces legal or similarly significant effects.</p>

				<h3 class="text-2xl font-funnel mt-8">
					Your rights
				</h3>

				<p>
					<strong>General</strong>. European data protection laws give you certain rights regarding your personal information. If you are located in Europe, you may ask us to take the following actions in relation to your personal information that we hold:
				</p>

				<ul class="list-disc pl-12">
					<li><b>Access.</b> Provide you with information about our processing of your personal information and give you access to your personal information.</li>
					<li><b>Correct.</b> Update or correct inaccuracies in your personal information.</li>
					<li><b>Delete.</b> Delete your personal information where there is no good reason for us continuing to process it - you also have the right to ask us to delete or remove your personal information where you have exercised your right to object to processing (see below).</li>
					<li><b>Transfer.</b> Transfer a machine-readable copy of your personal information to you or a third party of your choice.</li>
					<li><b>Restrict.</b> Restrict the processing of your personal information, for example if you want us to establish its accuracy or the reason for processing it.</li>
					<li><b>Object.</b> Object to our processing of your personal information where we are relying on Legitimate Interests – you also have the right to object where we are processing your personal information for direct marketing purposes.</li>
					<li><b>Withdraw Consent.</b> When we use your personal information based on your consent, you have the right to withdraw that consent at any time.</li>
				</ul>

				<p>
					<strong>Exercising These Rights.</strong> You may submit these requests by email to <span @click="revealEmail" :class="[emailRevealed ? '' : 'italic text-blue-500 underline']">{{ emailRevealed ? emailMasked : 'click to reveal.' }}</span> We may request specific information from you to help us confirm your identity and process your request. Whether or not we are required to fulfill any request you make will depend on a number of factors (e.g., why and how we are processing your personal information), if we reject any request you may make (whether in whole or in part) we will let you know our grounds for doing so at the time, subject to any legal restrictions.
				</p>

				<p>
					<strong>Your Right to Lodge a Complaint with your Supervisory Authority.</strong> In addition to your rights outlined above, if you are not satisfied with our response to a request you make, or how we process your personal information, you can make a complaint to the data protection regulator in your habitual place of residence.
				</p>

				<h3 class="text-2xl font-funnel mt-8">
					Data Processing outside Europe
				</h3>
				<p>
					We are based in the U.S. This means that, if you use the Service, your personal information will necessarily be accessed and processed in the U.S. It may also be provided to recipients in other countries outside Europe.</p>
				<p>
				It is important to note that the US is not the subject of an ‘adequacy decision’ under the GDPR – basically, this means that the U.S. legal regime is not considered by relevant European bodies to provide an adequate level of protection for personal information, which is equivalent to that provided by relevant European laws.</p>
				<p>
				Where we share your personal information with third parties who are based outside Europe, we try to ensure a similar degree of protection is afforded to it by making sure one of the following mechanisms is implemented:
				</p>

				<ul class="list-disc pl-12 space-y-2">
					<li><b>Transfers to territories with an adequacy decision.</b> We may transfer your personal information to countries or territories whose laws have been deemed to provide an adequate level of protection for personal information by the European Commission or UK Government (as and where applicable) (from time to time).</li>
					<li><b>Transfers to territories without an adequacy decision.</b></li>
					<li class="ml-8">We may transfer your personal information to countries or territories whose laws have not been deemed to provide such an adequate level of protection (e.g., the U.S., see above).</li>
					<li class="ml-8">
						However, in these cases:
					</li>
					<li class="ml-12">we may use specific appropriate safeguards, which are designed to give personal information effectively the same protection it has in Europe – for example, standard-form contracts approved by relevant authorise for this purpose; or</li>
					<li class="ml-12">in limited circumstances, we may rely on an exception, or ‘derogation’, which permits us to transfer your personal information to such country despite the absence of an ‘adequacy decision’ or ‘appropriate safeguards’ – for example, reliance on your explicit consent to that transfer.</li>
				</ul>
				<p>You may contact us if you want further information on the specific mechanism used by us when transferring your personal information out of Europe.</p>

			</section>

		</main>

		<Footer />

	</div>
</template>

<style scoped>

</style>